/**
 * Azure Speech-to-Text adapter.
 *
 * Implements the SpeechProvider interface (see types.js). This is the same
 * logic that currently lives inline in QuestionSection.jsx, packaged as a
 * factory so we can swap it for a different vendor (e.g. Sarvam) without
 * touching QuestionSection's call sites.
 *
 * IMPORTANT: Module-level globals (_globalRecognizer, _globalAudioStream,
 * _globalRecognizerInitialized) intentionally survive component remounts —
 * this is the React Strict Mode double-mount protection that the original
 * inline code relied on. Do not move them into the factory closure.
 *
 * Phase 2A: this file exists but QuestionSection.jsx does NOT use it yet.
 * The migration happens in Phase 2B.
 */

import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { AudioConfig, SpeechConfig, SpeechRecognizer } from 'microsoft-cognitiveservices-speech-sdk';

// ── Azure subscription config (build-time env vars) ─────────────────────────
const API_KEY = process.env.REACT_APP_COG_SERVICE_KEY;
const API_LOCATION = process.env.REACT_APP_COG_SERVICE_LOCATION;

// One SpeechConfig per page load is fine — it's just configuration.
const _speechConfig =
    API_KEY && API_LOCATION
        ? SpeechConfig.fromSubscription(API_KEY, API_LOCATION)
        : null;

// ── Module-level persistence for React Strict Mode ──────────────────────────
// These survive component remounts so a Strict-Mode double-invoke doesn't
// create two competing recognizers. The factory exposes reuseExisting() so
// the component can pick them back up after a remount.
let _globalRecognizer = null;
let _globalAudioStream = null;
let _globalRecognizerInitialized = false;

const MAX_RETRY_ATTEMPTS = 3;

/**
 * Factory — returns a SpeechProvider implementation backed by Azure.
 * @param {import('./types').SpeechProviderDeps} deps
 * @returns {import('./types').SpeechProvider}
 */
export function createAzureProvider(deps) {
    const {
        recognizerRef,
        audioStreamRef,
        recognitionActiveRef,
        recognitionRetryCount,
        interviewActiveRef,
        transcriptionRef,
        setIsRecognising,
        setRecognisingText,
        setTranscription,
        setErrorMessage,
        setConvTick,
        onUserMessage,
    } = deps;

    // ── Internal helpers ────────────────────────────────────────────────────

    async function createNewRecognizer() {
        console.log('[azure] createNewRecognizer called');

        try {
            // Force cleanup any existing recognizer
            await cleanup({ force: true });

            const constraints = {
                video: false,
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    sampleSize: 16,
                    volume: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            };

            const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
            audioStreamRef.current = audioStream;
            _globalAudioStream = audioStream;

            if (!_speechConfig) {
                console.error(
                    'Azure Speech configuration is missing. Please set REACT_APP_COG_SERVICE_KEY and REACT_APP_COG_SERVICE_LOCATION environment variables.'
                );
                return false;
            }

            const audioConfig = AudioConfig.fromStreamInput(audioStream);
            const recognizer = new SpeechRecognizer(_speechConfig, audioConfig);
            recognizerRef.current = recognizer;
            _globalRecognizer = recognizer;

            console.log('[azure] New recognizer created');

            // ── Event handlers ──────────────────────────────────────────────
            recognizer.recognizing = (s, e) => {
                const interimText = e.result.text;
                setRecognisingText(interimText);
                setConvTick((t) => t + 1);
            };

            recognizer.recognized = (s, e) => {
                if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
                    const newText = e.result.text;
                    setRecognisingText('');

                    // Append to existing transcription
                    transcriptionRef.current = transcriptionRef.current
                        ? `${transcriptionRef.current} ${newText}`
                        : newText;

                    setTranscription(transcriptionRef.current);

                    if (typeof onUserMessage === 'function') {
                        onUserMessage(newText);
                    }

                    // Reset retry count on successful recognition
                    recognitionRetryCount.current = 0;
                    setConvTick((t) => t + 1);
                } else if (e.result.reason === sdk.ResultReason.NoMatch) {
                    console.log('No speech could be recognized');
                }
            };

            recognizer.canceled = (s, e) => {
                let errorMsg = `Recognition canceled: ${e.reason}`;

                if (e.reason === sdk.CancellationReason.Error) {
                    errorMsg = `Recognition error (Code: ${e.errorCode}): ${e.errorDetails}`;
                    console.error(errorMsg);

                    // Check if this is an AudioNode/context error - need full reinit
                    const isAudioContextError =
                        e.errorDetails &&
                        (e.errorDetails.includes('AudioNode') ||
                            e.errorDetails.includes('audio context') ||
                            e.errorDetails.includes('context is closed'));

                    if (isAudioContextError) {
                        console.log('[azure] Audio context error detected - will reinitialize recognizer');
                        recognitionActiveRef.current = false;
                        setIsRecognising(false);
                        // Reinitialize the entire recognizer
                        setTimeout(async () => {
                            if (interviewActiveRef.current) {
                                console.log('[azure] Reinitializing recognizer after audio context error');
                                await createNewRecognizer();
                                await startRecognition();
                            }
                        }, 1000);
                        return;
                    }

                    // Handle specific error codes for network issues
                    if (
                        e.errorCode === sdk.CancellationErrorCode.ConnectionFailure ||
                        e.errorCode === sdk.CancellationErrorCode.ServiceTimeout
                    ) {
                        // Network-related errors - attempt retry with full reinit
                        if (recognitionRetryCount.current < MAX_RETRY_ATTEMPTS && recognitionActiveRef.current) {
                            recognitionRetryCount.current++;
                            console.log(
                                `[azure] Retrying recognition with reinit (attempt ${recognitionRetryCount.current}/${MAX_RETRY_ATTEMPTS})`
                            );
                            setTimeout(async () => {
                                if (recognitionActiveRef.current || interviewActiveRef.current) {
                                    await reinitializeAndStartRecognition();
                                }
                            }, 1000 * recognitionRetryCount.current); // Exponential backoff
                            return;
                        }
                    }
                }

                setErrorMessage(errorMsg);
                recognitionActiveRef.current = false;
                setIsRecognising(false);
            };

            recognizer.sessionStopped = (s, e) => {
                console.log('[azure] Session stopped event.');
                recognitionActiveRef.current = false;
                setIsRecognising(false);
            };

            recognizer.sessionStarted = (s, e) => {
                console.log('[azure] Session started event.');
                setErrorMessage('');
            };

            // Add speech start/end detection for better responsiveness
            recognizer.speechStartDetected = (s, e) => {
                console.log('[azure] Speech started');
            };

            recognizer.speechEndDetected = (s, e) => {
                console.log('[azure] Speech ended');
            };

            _globalRecognizerInitialized = true;
            console.log('[azure] Recognizer initialized successfully');
            return true;
        } catch (error) {
            console.error('[azure] Error creating recognizer:', error);
            setErrorMessage(`Initialization error: ${error.message}`);
            _globalRecognizerInitialized = false;
            return false;
        }
    }

    async function reinitializeAndStartRecognition() {
        console.log('[azure] reinitializeAndStartRecognition called');
        const success = await createNewRecognizer();
        if (success) {
            await startRecognition();
        }
    }

    async function startRecognition() {
        // Try to use ref first, fall back to global
        const recognizer = recognizerRef.current || _globalRecognizer;

        if (!recognizer) {
            console.error('[azure] Recognizer not initialized - will create new one');
            // Try to create a new recognizer
            const success = await createNewRecognizer();
            if (!success) {
                console.error('[azure] Failed to create recognizer');
                return;
            }
        }

        // Ensure ref is set
        if (!recognizerRef.current && _globalRecognizer) {
            recognizerRef.current = _globalRecognizer;
        }

        if (recognitionActiveRef.current) {
            console.log('[azure] Recognition already active');
            return;
        }

        try {
            console.log('[azure] Starting recognition...');
            recognitionActiveRef.current = true;
            const currentRecognizer = recognizerRef.current || _globalRecognizer;

            await currentRecognizer.startContinuousRecognitionAsync(
                () => {
                    console.log('[azure] Recognition started successfully');
                    setIsRecognising(true);
                    setRecognisingText('');
                    setErrorMessage('');
                },
                async (error) => {
                    console.error('[azure] Failed to start recognition:', error);
                    recognitionActiveRef.current = false;
                    setIsRecognising(false);

                    // Check if this is an AudioNode error - need full reinit
                    const errorStr = String(error);
                    if (
                        errorStr.includes('AudioNode') ||
                        errorStr.includes('disconnect') ||
                        errorStr.includes('audio context')
                    ) {
                        console.log('[azure] Audio context error in start - will reinitialize');
                        // Don't retry immediately, let the caller handle it
                        setErrorMessage('Audio error - reinitializing...');
                        // Schedule reinit
                        setTimeout(async () => {
                            if (interviewActiveRef.current) {
                                await reinitializeAndStartRecognition();
                            }
                        }, 500);
                    } else {
                        setErrorMessage(`Failed to start: ${error}`);
                    }
                }
            );
        } catch (error) {
            console.error('[azure] Error in startAzureRecognition:', error);
            recognitionActiveRef.current = false;
            setIsRecognising(false);

            // Check if this is an AudioNode error
            const errorStr = String(error);
            if (
                errorStr.includes('AudioNode') ||
                errorStr.includes('disconnect') ||
                errorStr.includes('audio context')
            ) {
                console.log('[azure] Audio context exception - will reinitialize');
                setTimeout(async () => {
                    if (interviewActiveRef.current) {
                        await reinitializeAndStartRecognition();
                    }
                }, 500);
            }
        }
    }

    async function stopRecognition() {
        const recognizer = recognizerRef.current || _globalRecognizer;

        if (!recognizer || !recognitionActiveRef.current) {
            console.log('[azure] stopAzureRecognition - nothing to stop');
            return;
        }

        console.log('[azure] Stopping recognition...');
        recognitionActiveRef.current = false;

        return new Promise((resolve) => {
            try {
                recognizer.stopContinuousRecognitionAsync(
                    () => {
                        console.log('[azure] Recognition stopped successfully');
                        setIsRecognising(false);
                        setRecognisingText('');
                        resolve();
                    },
                    (error) => {
                        console.error('[azure] Error stopping recognition:', error);
                        setIsRecognising(false);
                        resolve();
                    }
                );
            } catch (e) {
                console.log('[azure] Exception in stopAzureRecognition:', e.message);
                setIsRecognising(false);
                resolve();
            }
        });
    }

    async function restart() {
        console.log('[azure] Restarting recognition with full reinit...');
        await reinitializeAndStartRecognition();
    }

    async function cleanup({ force = false } = {}) {
        console.log('[azure] cleanupRecognizer called, force:', force);

        // If not forcing, don't cleanup (React Strict Mode protection)
        if (!force && _globalRecognizerInitialized) {
            console.log('[azure] Skipping cleanup - recognizer should persist');
            return;
        }

        const recognizer = recognizerRef.current || _globalRecognizer;
        const audioStream = audioStreamRef.current || _globalAudioStream;

        if (recognizer) {
            recognitionActiveRef.current = false;

            try {
                await stopRecognition();
                recognizer.close();
                console.log('[azure] Recognizer closed');
            } catch (error) {
                console.error('Error cleaning up recognizer:', error);
            }
        }

        recognizerRef.current = null;
        _globalRecognizer = null;
        _globalRecognizerInitialized = false;

        if (audioStream) {
            try {
                audioStream.getTracks().forEach((track) => track.stop());
                console.log('[azure] Audio stream tracks stopped');
            } catch (e) {
                console.log('[azure] Error stopping audio tracks:', e.message);
            }
        }

        audioStreamRef.current = null;
        _globalAudioStream = null;
    }

    function reuseExisting() {
        // For React Strict Mode: reuse the existing recognizer/stream if they
        // were created during a previous mount. Returns true if reuse happened.
        if (_globalRecognizerInitialized && _globalRecognizer) {
            console.log('[azure] Reusing existing recognizer from previous mount');
            recognizerRef.current = _globalRecognizer;
            audioStreamRef.current = _globalAudioStream;
            return true;
        }
        return false;
    }

    function isInitialized() {
        return _globalRecognizerInitialized;
    }

    return {
        providerName: 'azure',
        createNewRecognizer,
        reinitializeAndStartRecognition,
        startRecognition,
        stopRecognition,
        restart,
        cleanup,
        reuseExisting,
        isInitialized,
    };
}
